import { useCallback } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { uniq } from 'lodash'

import { SHOW_SMALL_TRANSFERS_KEY } from '~/constants/localStorage'
import { QUERY_KEYS } from '~/constants/react-query'
import { REFETCH_ASSET_TRANSFERS } from '~/constants/refetch-intervals'
import { useNotifications } from '~/context/notificationContext'
import { useActiveOrgId } from '~/hooks/org'
import { useAccountingUpdates } from '~/hooks/useAccountingUpdates'
import { useServerRequests } from '~/hooks/useRequests'
import { useShowDust } from '~/hooks/useShowDust'
import { AssetType, OrgAssetTransfer } from '~/types'

const SMALL_TRANSFER_THRESHOLD_USD = 1

const shouldNotifyInboundTransfer = ({
  transfer,
  showSmallTransfers,
}: {
  transfer: OrgAssetTransfer
  showSmallTransfers: boolean
}) =>
  showSmallTransfers ||
  transfer.assetType === AssetType.ERC721 ||
  transfer.assetType === AssetType.ERC1155 ||
  (transfer.value !== undefined &&
    Number(transfer.value) >= SMALL_TRANSFER_THRESHOLD_USD)

export const useAssetTransfer = (
  assetTransferId: string,
): {
  assetTransfer: OrgAssetTransfer | undefined
  isLoading: boolean
} => {
  const { serverGetRequest } = useServerRequests()

  const { data: assetTransfer, isLoading } = useQuery({
    queryKey: [QUERY_KEYS.TRANSFER, assetTransferId],
    queryFn: async () => {
      const result = await serverGetRequest<{
        transfer: OrgAssetTransfer
      }>({
        path: `/api/smart_accounts/transactions/transfers/${assetTransferId}`,
      })

      if (!result?.data) {
        throw new Error('No transaction found')
      }

      return result?.data.transfer
    },
  })

  return {
    assetTransfer,
    isLoading,
  }
}

export const useLatestAssetTransferNotifications = () => {
  const { serverGetRequest } = useServerRequests()
  const activeOrgId = useActiveOrgId()
  const { sendInboundToastNotification } = useNotifications()
  const queryClient = useQueryClient()
  const [showSmallTransfers] = useShowDust(SHOW_SMALL_TRANSFERS_KEY)

  useQuery({
    queryKey: [
      QUERY_KEYS.LATEST_TRANSFERS,
      activeOrgId,
      sendInboundToastNotification,
    ],
    queryFn: async () => {
      if (!activeOrgId) throw new Error('Auth required')

      const result = await serverGetRequest<{
        transfers: OrgAssetTransfer[]
      }>({
        path: `/api/smart_accounts/transactions/transfers/latest`,
        params: {
          orgId: activeOrgId,
        },
      })

      if (!result) {
        throw new Error('Failed to fetch latest transfers')
      }

      const transfers = result.data.transfers

      // Reloading the balances for the smart accounts present in the transfers
      const smartAccountAddresses = uniq(
        transfers.map((t) => t.smartAccountAddress),
      )
      smartAccountAddresses.forEach((address) => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.SMART_ACCOUNT_BALANCES, address],
        })
      })

      transfers
        .filter((transfer) =>
          shouldNotifyInboundTransfer({
            transfer,
            showSmallTransfers,
          }),
        )
        .forEach(sendInboundToastNotification)

      return transfers
    },
    refetchInterval: REFETCH_ASSET_TRANSFERS,
    staleTime: Infinity,
    enabled: !!activeOrgId,
  })
}

export const useUpdateAssetTransferMemo = (): {
  updateMemo: (assetTransfer: OrgAssetTransfer, memo: string) => Promise<void>
  isProcessing: boolean
} => {
  const { isProcessing, serverPutRequest } = useServerRequests()
  const queryClient = useQueryClient()
  const { updateMemo: updateAccountingMemo } = useAccountingUpdates()

  const updateMemo = useCallback(
    async (assetTransfer: OrgAssetTransfer, memo: string) => {
      const result = await serverPutRequest<{ memo: string | undefined }>({
        path: `/api/smart_accounts/transactions/transfers/${assetTransfer.id}/memo`,
        params: {
          memo,
        },
        errorMessage: 'Failed to update memo',
      })

      if (result) {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.TRANSFER, assetTransfer.id],
        })
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TRANSACTIONS] })
        updateAccountingMemo(assetTransfer.id, memo)
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.ORG_ACCOUNTING_ITEMS],
        })
      }
    },
    [queryClient, serverPutRequest, updateAccountingMemo],
  )

  return {
    updateMemo,
    isProcessing,
  }
}
